import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { MessageDeliveryStatus } from "@/types/message-delivery";
import { ResolvedChannelValue } from "@/types/chat-external-message";

interface MessageDeliveryFailureProps {
  status?: MessageDeliveryStatus;
  errorCode?: string;
  channel?: ResolvedChannelValue;
}

const FAILED_STATUSES: readonly MessageDeliveryStatus[] = [
  "FAILED",
  "REJECTED",
];

export const isFailedDeliveryStatus = (
  status?: MessageDeliveryStatus,
): boolean => !!status && FAILED_STATUSES.includes(status);

export const MessageDeliveryFailure: FC<MessageDeliveryFailureProps> = ({
  status,
  errorCode,
  channel,
}) => {
  const { t } = useTranslation();

  if (!isFailedDeliveryStatus(status)) return null;

  const headline =
    channel && channel !== "STREAM_ONLY"
      ? t("communicationChannels.messageDelivery.failedVia", {
          channel: t(`communicationChannels.composer.badge.${channel}`),
        })
      : t("communicationChannels.messageDelivery.failed");

  return (
    <div
      className={cn(
        "mt-1 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs",
        "bg-destructive/5 text-destructive",
      )}
      role="alert"
    >
      <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
      <span>{headline}</span>
      {errorCode && (
        <span className="font-mono text-[10px] opacity-70">
          {t("communicationChannels.messageDelivery.errorCode", {
            code: errorCode,
          })}
        </span>
      )}
    </div>
  );
};
